import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["widget", "messages", "input", "badge"]
  static values = { 
    pollInterval: { type: Number, default: 3000 }
  }

  connect() {
    this.isOpen = false
    this.lastMessageId = null
    this.pollTimer = null
    this.messagesLoaded = false
    this.messagesLoading = false
  }

  disconnect() {
    this.stopPolling()
  }

  toggle() {
    this.isOpen = !this.isOpen
    
    if (this.isOpen) {
      this.widgetTarget.classList.remove("hidden")
      this.inputTarget.focus()
      if (!this.messagesLoaded && !this.messagesLoading) this.loadMessages()
      this.startPolling()
      this.clearBadge()
      this.scrollToBottom()
    } else {
      this.widgetTarget.classList.add("hidden")
      this.stopPolling()
    }
  }

  async sendMessage(event) {
    event.preventDefault()
    
    const message = this.inputTarget.value.trim()
    if (!message) return

    try {
      const response = await fetch("/support_messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ message })
      })

      if (response.ok) {
        const newMessage = await response.json()
        this.addMessage(newMessage)
        this.lastMessageId = newMessage.id  // Prevent duplicate from polling
        this.inputTarget.value = ""
        this.scrollToBottom()
      }
    } catch (error) {
      console.error("Failed to send message:", error)
      this.showError("Failed to send message. Please try again.")
    }
  }

  async loadMessages() {
    this.messagesLoading = true

    try {
      const response = await fetch("/support_messages")
      if (response.ok) {
        const messages = await response.json()
        this.renderMessages(messages)
        this.messagesLoaded = true
        
        if (messages.length > 0) {
          this.lastMessageId = messages[messages.length - 1].id
        }
      }
    } catch (error) {
      console.error("Failed to load messages:", error)
    } finally {
      this.messagesLoading = false
    }
  }

  async pollMessages() {
    try {
      const response = await fetch("/support_messages")
      if (response.ok) {
        const messages = await response.json()
        
        // Check for new messages
        const newMessages = messages.filter(msg => 
          this.lastMessageId === null || msg.id > this.lastMessageId
        )
        
        if (newMessages.length > 0) {
          newMessages.forEach(msg => this.addMessage(msg))
          this.lastMessageId = messages[messages.length - 1].id
          
          // Show notification if widget is closed
          if (!this.isOpen) {
            this.showBadge(newMessages.length)
          }
          
          this.scrollToBottom()
        }
      }
    } catch (error) {
      console.error("Failed to poll messages:", error)
    }
  }

  renderMessages(messages) {
    this.messagesTarget.innerHTML = ""
    messages.forEach(msg => this.addMessage(msg, false))
    this.scrollToBottom()
  }

  addMessage(message, animate = true) {
    const messageEl = document.createElement("div")
    messageEl.className = `flex ${message.is_staff_reply ? 'justify-start' : 'justify-end'} mb-3`
    
    const bubbleClass = message.is_staff_reply 
      ? 'bg-gray-100 text-gray-900' 
      : 'bg-primary-600 text-white'
    
    messageEl.innerHTML = `
      <div class="max-w-[75%] ${animate ? 'animate-fade-in' : ''}">
        <div class="px-4 py-2 rounded-2xl ${bubbleClass}">
          <p class="text-sm">${this.escapeHtml(message.message)}</p>
        </div>
        <p class="text-xs text-gray-500 mt-1 px-2">
          ${this.formatTime(message.created_at)}
        </p>
      </div>
    `
    
    this.messagesTarget.appendChild(messageEl)
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  startPolling() {
    this.stopPolling()
    this.pollTimer = setInterval(() => {
      this.pollMessages()
    }, this.pollIntervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  showBadge(count) {
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = count
      this.badgeTarget.classList.remove("hidden")
    }
  }

  clearBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add("hidden")
    }
  }

  showError(message) {
    // Simple error display - could be enhanced with a toast notification
    const errorEl = document.createElement("div")
    errorEl.className = "text-red-600 text-sm p-2 mb-2 bg-red-50 rounded"
    errorEl.textContent = message
    this.messagesTarget.appendChild(errorEl)
    
    setTimeout(() => errorEl.remove(), 3000)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  formatTime(timestamp) {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    
    return date.toLocaleDateString()
  }

  csrfToken() {
    return document.querySelector("[name='csrf-token']").content
  }
}
